import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Upload, X, Loader2, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MultiImageUploader
 *
 * Manages a list of photos for a car. The FIRST photo is the main one (used as
 * the cover everywhere). Admin can:
 *  - upload several photos at once (Cloudinary)
 *  - reorder by clicking "set as main"
 *  - remove individual photos
 *
 * Output shape via onChange({ main, additional }):
 *   - main:       string (the cover image url, "" if none)
 *   - additional: string[] (other photos, in display order)
 */
const MultiImageUploader = ({ value, onChange }) => {
  // value = { main: string, additional: string[] }
  const [items, setItems] = useState(() => {
    const list = [];
    if (value?.main) list.push(value.main);
    (value?.additional || []).forEach((u) => {
      if (u && !list.includes(u)) list.push(u);
    });
    return list;
  });
  const [uploading, setUploading] = useState(false);

  // Sync up when parent resets (edit -> add another car)
  useEffect(() => {
    const list = [];
    if (value?.main) list.push(value.main);
    (value?.additional || []).forEach((u) => {
      if (u && !list.includes(u)) list.push(u);
    });
    setItems(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.main, JSON.stringify(value?.additional)]);

  const emit = (next) => {
    onChange({
      main: next[0] || '',
      additional: next.slice(1),
    });
  };

  const updateItems = (next) => {
    setItems(next);
    emit(next);
  };

  const uploadToCloudinary = async (file) => {
    const sigResponse = await axios.get(`${BACKEND_URL}/api/cloudinary/signature`);
    const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', api_key);
    formData.append('timestamp', timestamp);
    formData.append('signature', signature);
    formData.append('folder', folder);

    const uploadResponse = await axios.post(
      `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
      formData
    );
    return uploadResponse.data.secure_url;
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newUrls = [];
    let okCount = 0;
    let failed = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        if (!file.type.startsWith('image/')) {
          failed.push(`${file.name} (pas une image)`);
          continue;
        }
        if (file.size > 15 * 1024 * 1024) {
          failed.push(`${file.name} (>15 Mo)`);
          continue;
        }
        try {
          const url = await uploadToCloudinary(file);
          newUrls.push(url);
          okCount += 1;
        } catch (err) {
          console.error('Upload error for', file.name, err);
          failed.push(file.name);
        }
      }
      if (newUrls.length > 0) {
        const next = [...items, ...newUrls];
        updateItems(next);
        toast.success(`${okCount} photo(s) ajoutée(s)`);
      }
      if (failed.length > 0) {
        toast.error(`Échec : ${failed.slice(0, 3).join(', ')}${failed.length > 3 ? '…' : ''}`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Échec de l'upload. Vérifiez la configuration Cloudinary.");
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const removeAt = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    updateItems(next);
  };

  const setAsMain = (idx) => {
    if (idx === 0) return;
    const next = [items[idx], ...items.filter((_, i) => i !== idx)];
    updateItems(next);
    toast.success('Photo principale mise à jour');
  };

  return (
    <div className="space-y-4">
      <div>
        <input
          type="file"
          id="multi-image-upload"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        <label htmlFor="multi-image-upload">
          <Button
            type="button"
            onClick={() => document.getElementById('multi-image-upload').click()}
            disabled={uploading}
            className="btn-secondary w-full"
            asChild
          >
            <span>
              {uploading ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={18} />
                  Upload en cours…
                </>
              ) : (
                <>
                  <Upload className="mr-2" size={18} />
                  Ajouter des photos
                </>
              )}
            </span>
          </Button>
        </label>
        <p className="text-xs text-gray-400 mt-2">
          Sélectionnez plusieurs photos en une fois (max 15 Mo chacune). La première photo sera utilisée comme photo principale.
          Cliquez sur l'étoile pour changer la photo principale.
        </p>
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((url, index) => (
            <div key={`${url}-${index}`} className="relative group" data-testid={`car-photo-${index}`}>
              <img
                src={url}
                alt={`Photo ${index + 1}`}
                className={`w-full h-28 object-cover rounded border ${index === 0 ? 'border-[#C9A961] border-2' : 'border-gold-500/30'}`}
              />
              {index === 0 && (
                <span className="absolute top-1 left-1 bg-[#C9A961] text-black text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold flex items-center gap-1" data-testid="main-photo-badge">
                  <Star size={10} fill="black" /> Principale
                </span>
              )}
              {index !== 0 && (
                <button
                  type="button"
                  onClick={() => setAsMain(index)}
                  className="absolute top-1 left-1 bg-black/85 hover:bg-[#C9A961] hover:text-black text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-all flex items-center gap-1"
                  title="Définir comme photo principale"
                  data-testid={`set-main-${index}`}
                >
                  <Star size={10} /> Principale
                </button>
              )}
              <button
                type="button"
                onClick={() => removeAt(index)}
                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Supprimer"
                data-testid={`remove-photo-${index}`}
              >
                <X size={12} />
              </button>
              <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5">
                {index + 1}/{items.length}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiImageUploader;
